import { useEffect } from "react";
import LakewoodRanchHeroSection from "./components/HeroSection";
import LakewoodRanchStickyNav from "./components/StickyNav";
import LakewoodRanchProcessingTimeSection from "./components/ProcessingTimeSection";
import LakewoodRanchServicesSection from "./components/ServicesSection";
import LakewoodRanchLocalAreaSection from "./components/LocalAreaSection";
import LakewoodRanchTravelTimingSection from "./components/TravelTimingSection";
import LakewoodRanchDestinationsSection from "./components/DestinationsSection";
import LakewoodRanchTestimonialsSection from "./components/TestimonialsSection";
import LakewoodRanchAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import LakewoodRanchComparisonSection from "./components/ComparisonSection";
import LakewoodRanchMistakesSection from "./components/MistakesSection";
import LakewoodRanchHowItWorksSection from "./components/HowItWorksSection";
import LakewoodRanchFAQSection, { faqs } from "./components/FAQSection";
import LakewoodRanchHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import LakewoodRanchFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function LakewoodRanchPage() {
  useEffect(() => {
    document.title = "Emergency Passport Lakewood Ranch, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/lakewood-ranch/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Lakewood Ranch, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Sarasota and Manatee County families, golf travelers, and school trip organizers.");
    setMeta("geo.placename", "Lakewood Ranch, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "27.4505;-82.4315");
    setMeta("ICBM", "27.4505, -82.4315");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "Lakewood Ranch Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Lakewood Ranch — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Lakewood Ranch and Sarasota / Manatee County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Lakewood Ranch",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Lakewood Ranch",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <LakewoodRanchHeroSection />
      <LakewoodRanchStickyNav />
      <LakewoodRanchProcessingTimeSection />
      <LakewoodRanchServicesSection />
      <LakewoodRanchLocalAreaSection />
      <LakewoodRanchTravelTimingSection />
      <LakewoodRanchDestinationsSection />
      <LakewoodRanchTestimonialsSection />
      <LakewoodRanchAcceptanceFacilitiesSection />
      <LakewoodRanchComparisonSection />
      <LakewoodRanchMistakesSection />
      <LakewoodRanchHowItWorksSection />
      <LakewoodRanchFAQSection />
      <LakewoodRanchHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/lakewood-ranch" state="FL" />
      <LakewoodRanchFinalCTA />
    </div>
  );
}